// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 20-agent-plugins.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0040-multiple-plugin-marketplace-repositories.md

import * as React from 'react';
import { CircleDashed, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Disclosure } from '@/components/greenlight/Disclosure';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ALL_AGENT_KINDS,
  type AgentKind,
  type OrgRole,
  type OrgSettings,
} from '@greenlight/shared';

import { AgentInstructions } from './AgentInstructions';
import { AgentKindMark } from './AgentMark';
import { AgentVisibilityNote } from './AgentVisibilityNote';
import { MARKETPLACE_CREATE_PATH, OrgAdminContacts, pluginSetupState } from './pluginSetup';
import { SettingsSectionHeading } from './SettingsSectionHeading';

export interface AgentSetupProps {
  agentSetup: OrgSettings['agent_setup'];
  /**
   * The viewer's platform role. Per-user instructions render for everyone; the
   * org-level rollout cards only for IT (docs/28 §Agent setup). Absent — an
   * unresolved identity — reveals nothing.
   */
  userRole?: OrgRole;
  /** The Settings section heading; the install wizard embeds the component under its own step title. */
  showHeading?: boolean;
  className?: string;
}

const AGENT_LABELS: Record<AgentKind, string> = {
  claude: 'Claude',
  codex: 'Codex',
  cursor: 'Cursor',
  copilot: 'Copilot',
  other: 'Other',
};

const AGENT_OPTIONS = ALL_AGENT_KINDS.map((value) => ({
  value,
  label: AGENT_LABELS[value],
}));

function MarketplaceRolloutCta(): React.ReactElement {
  return (
    <SectionCard title="Roll out the Greenlight plugin to your org" headerBar>
      <div
        className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center"
        data-testid="agent-setup-not-bootstrapped"
      >
        <Rocket className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <p className="flex-1 text-sm text-muted-foreground">
          Agents install the plugin from a Greenlight agent plugin repo your org owns. Create it and
          the install steps for every agent appear here.
        </p>
        <Button asChild size="sm" className="shrink-0">
          <Link to={MARKETPLACE_CREATE_PATH}>Create the agent plugin repo</Link>
        </Button>
      </div>
    </SectionCard>
  );
}

function MarketplaceWaiting({
  admins,
}: {
  admins: OrgSettings['agent_setup']['org_admins'];
}): React.ReactElement {
  return (
    <SectionCard title="Your admin hasn't set up the Greenlight plugin yet" headerBar>
      <div className="flex gap-3 px-5 py-4" data-testid="agent-setup-not-bootstrapped">
        <CircleDashed
          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
        <p className="text-sm text-muted-foreground" data-testid="agent-setup-admins">
          There are no install steps until an org admin creates the Greenlight agent plugin repo.{' '}
          <OrgAdminContacts admins={admins} />
        </p>
      </div>
    </SectionCard>
  );
}

function AboutAgentSetup(): React.ReactElement {
  return (
    <Disclosure label="About the Greenlight plugin" testId="agent-setup-about">
      <div className="max-w-prose space-y-2 text-sm text-muted-foreground">
        <p>
          The plugin&rsquo;s portable core — Agent Skills and Streamable HTTP MCP — conforms to
          Agent Plugins 1.0. Installation, org policy, identity, and authorization stay
          client-specific and Greenlight-governed.
        </p>
        <p>
          Every managed agent plugin repo serves the same plugin. The instructions always use the
          selected agent and repo; Greenlight does not hide or prefer one.
        </p>
      </div>
    </Disclosure>
  );
}

export function AgentSetup({
  agentSetup,
  userRole,
  showHeading = true,
  className,
}: AgentSetupProps): React.JSX.Element {
  const showOrgRollout = userRole === 'org_admin' || userRole === 'admin';
  const repositories = agentSetup.marketplace_repositories;
  const [agent, setAgent] = React.useState<AgentKind>(ALL_AGENT_KINDS[0]);
  const [repoFullName, setRepoFullName] = React.useState(repositories[0]?.repo_full_name ?? '');

  // A stale selection (row removed or renamed) falls back to the first row.
  const repository =
    repositories.find((candidate) => candidate.repo_full_name === repoFullName) ??
    repositories[0] ??
    null;

  const setupState = pluginSetupState(userRole, repository !== null);
  const showRepositoryPicker = repositories.length > 1;

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      {showHeading && <SettingsSectionHeading title="Agent setup" pill="Everyone" />}

      {repository === null ? (
        setupState === 'admin-rollout' ? (
          <MarketplaceRolloutCta />
        ) : (
          <MarketplaceWaiting admins={agentSetup.org_admins} />
        )
      ) : (
        <>
          <p className="max-w-prose text-sm text-muted-foreground" data-testid="agent-setup-lead">
            {setupState === 'admin-ready'
              ? 'Follow the steps below to install Greenlight for everyone in your organization.'
              : 'Greenlight is usually already in your coding agent — IT rolls it out for everyone. If it is not, the self-serve steps below add it. Then describe the app you want; your agent builds and deploys it through Greenlight.'}
          </p>
          <div
            className={cn(
              'grid gap-3 rounded-md border bg-card p-4',
              showRepositoryPicker && 'md:grid-cols-[12rem_minmax(0,1fr)]',
            )}
          >
            <div className="space-y-2">
              <Label htmlFor="agent-setup-agent">Agent</Label>
              <Select value={agent} onValueChange={(value) => setAgent(value as AgentKind)}>
                <SelectTrigger id="agent-setup-agent" aria-label="Agent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AGENT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <span className="flex items-center gap-2">
                        <AgentKindMark agent={option.value} />
                        {option.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {showRepositoryPicker && (
              <div className="min-w-0 space-y-2">
                <Label htmlFor="agent-setup-repository">Agent plugin repo</Label>
                <Select value={repository.repo_full_name} onValueChange={setRepoFullName}>
                  <SelectTrigger
                    id="agent-setup-repository"
                    aria-label="Agent plugin repo"
                    className="min-w-0 overflow-hidden [&>svg]:shrink-0"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {repositories.map((option) => (
                      <SelectItem key={option.repo_full_name} value={option.repo_full_name}>
                        <span className="font-mono text-xs">{option.repo_full_name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          · {option.visibility === 'public' ? 'Public' : 'Private'}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <AgentVisibilityNote
            agent={agent}
            agentLabel={AGENT_LABELS[agent]}
            repository={repository}
            repositories={repositories}
            privateInstall={agentSetup.private_install === true}
          />

          <AgentInstructions
            agent={agent}
            repository={repository}
            agentSetup={agentSetup}
            showOrgRollout={showOrgRollout}
          />
        </>
      )}

      <AboutAgentSetup />
    </div>
  );
}
